import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import api from "../api";
import "./Table.css";
import "./Commissions.css";

const fmt  = n => "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = d => d ? new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

const STATUS_LABEL = {
  future:  "Future",
  current: "Current",
  paid:    "Paid",
};

const STMT_LABEL = {
  draft:                   "Draft",
  admin_review:            "Admin Review",
  manager_review:          "Manager Review",
  lo_review:               "Awaiting Approval",
  lo_disputed:             "Disputed",
  dispute_manager_review:  "Dispute — Manager",
  dispute_admin_review:    "Dispute — Admin",
  approved:                "Approved",
  paid:                    "Paid",
};

function periodLabel(p) {
  return `${fmtD(p.period_start)} – ${fmtD(p.period_end)}`;
}

export default function Commissions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [periods, setPeriods]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError]           = useState(null);

  const isAdmin   = user?.role === "admin";
  const isManager = user?.role === "manager";

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get("/payroll/periods")
      .then(r => setPeriods(r.data))
      .catch(err => setError(err.response?.data?.detail || "Failed to load pay periods"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/payroll/periods/generate");
      load();
      alert(data.message);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to generate periods");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Pay Periods</h1>
          <p className="page-subtitle">
            {loading ? "Loading..." : `${periods.length} period${periods.length !== 1 ? "s" : ""}`}
          </p>
        </div>
        {isAdmin && (
          <button className="btn-secondary btn-sm" onClick={handleGenerate} disabled={generating}>
            {generating ? "Generating..." : "Generate Periods"}
          </button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {loading ? (
        <div className="loading">Loading pay periods...</div>
      ) : periods.length === 0 ? (
        <div className="table-card">
          <div className="empty-state" style={{ padding: "48px 24px" }}>
            <div className="empty-icon">📅</div>
            <p>No pay periods found.</p>
            {isAdmin && (
              <button className="btn-primary btn-sm" style={{ marginTop: 12 }} onClick={handleGenerate}>
                Generate from Loan Data
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Period</th>
                <th>Pay Date</th>
                <th>Loans</th>
                {(isManager || isAdmin) && <th>LOs</th>}
                <th>Volume</th>
                <th>Commission</th>
                <th>Status</th>
                {!isManager && <th>Statement</th>}
              </tr>
            </thead>
            <tbody>
              {periods.length === 0 && (
                <tr><td colSpan={8} className="empty-row">No pay periods</td></tr>
              )}
              {periods.map(p => (
                <tr
                  key={p.id}
                  className="clickable-row"
                  onClick={() => navigate(`/commissions/period/${p.id}`)}
                >
                  <td className="period-label">{periodLabel(p)}</td>
                  <td>{fmtD(p.pay_date)}</td>
                  <td>{p.total_loans}</td>
                  {(isManager || isAdmin) && <td>{p.lo_count}</td>}
                  <td className="mono">{fmt(p.total_volume)}</td>
                  <td className="mono" style={{ color: "var(--success)" }}>{fmt(p.total_commission)}</td>
                  <td>
                    <span className={`badge badge-period-${p.status}`}>
                      {STATUS_LABEL[p.status] || p.status}
                    </span>
                  </td>
                  {!isManager && (
                    <td>
                      {p.statement_status ? (
                        <span className={`badge badge-stmt badge-stmt-${p.statement_status.replace(/_/g, "-")}`}>
                          {STMT_LABEL[p.statement_status] || p.statement_status}
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
